<script setup lang="ts">
import { computed, ref } from 'vue'
import draggable from 'vuedraggable'

import BaseImage from '@/components/atoms/BaseImage.vue'
import BetaPageCard from '@/components/beta-dashboard/molecules/BetaPageCard.vue'
import BetaPanelHeading from '@/components/beta-dashboard/molecules/BetaPanelHeading.vue'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import type { BetaCreatePageKind, BetaPage } from '@/types/beta-dashboard'

const props = defineProps<{
    selectedPageId?: number | null
    creatingPost?: boolean
}>()

const emit = defineEmits<{
    selectPage: [page: BetaPage | null]
    editPage: [page: BetaPage]
    configurePage: [page: BetaPage]
    editPost: [postId: number, pageId: number]
    openCreate: [kind?: BetaCreatePageKind]
    createPost: [pageId: number]
}>()

const betaStore = useBetaDashboardStore()
const search = ref('')
const searching = computed(() => Boolean(search.value.trim()))
const orderDisabled = computed(() => searching.value || betaStore.reorderingPages || betaStore.loadingContent)
const orderDisabledReason = computed(() => searching.value
    ? 'Limpe a busca para ordenar'
    : 'Aguarde a atualização das páginas')

const selectedPage = computed(() => (
    betaStore.pages.find((page) => page.id === props.selectedPageId)
    ?? null
))

const pagePosts = computed(() => (
    selectedPage.value ? betaStore.postsByPage[selectedPage.value.id] ?? [] : []
))

const filteredPages = computed(() => {
    const term = search.value.trim().toLocaleLowerCase()
    if (!term) return betaStore.pages

    return betaStore.pages.filter((page) => (
        String(page.name).toLocaleLowerCase().includes(term)
    ))
})

const filteredHome = computed(() => filteredPages.value.find((page) => page.id === betaStore.homePage?.id))
const sortablePages = computed(() => filteredPages.value.filter((page) => page.id !== betaStore.homePage?.id))

const reorderPages = (pages: BetaPage[]) => {
    if (orderDisabled.value) return
    void betaStore.reorderPages(betaStore.homePage ? [betaStore.homePage, ...pages] : pages)
}

const openPage = async (page: BetaPage) => {
    if (page.listPages == 1) {
        await betaStore.fetchPostsForPage(page.id)
        emit('selectPage', page)
        return
    }

    emit('editPage', page)
}
</script>

<template>
    <div class="beta-content">
        <BetaPanelHeading v-if="!selectedPage" title="Páginas">
            <template #actions>
                <button
                    type="button"
                    class="beta-content__primary"
                    @click="emit('openCreate')"
                >
                    + Nova página
                </button>
            </template>
        </BetaPanelHeading>

        <header v-else class="beta-content__heading">
            <div>
                <button
                    type="button"
                    class="beta-content__back"
                    @click="emit('selectPage', null)"
                >
                    ← Todas as páginas
                </button>
                <p>Páginas</p>
                <h1>{{ selectedPage.name }}</h1>
                <span>Gerencie as publicações desta página.</span>
            </div>

            <button
                type="button"
                class="beta-content__primary"
                :disabled="props.creatingPost"
                @click="emit('createPost', selectedPage.id)"
            >
                {{ props.creatingPost ? 'Criando…' : '+ Nova publicação' }}
            </button>
        </header>

        <template v-if="selectedPage">
            <div class="beta-content__toolbar">
                <div>
                    <strong>{{ pagePosts.length }}</strong>
                    <span>{{ pagePosts.length === 1 ? 'publicação' : 'publicações' }}</span>
                </div>
                <button type="button" @click="emit('configurePage', selectedPage)">
                    Configurar página
                </button>
            </div>

            <div v-if="betaStore.loadingContent" class="beta-content__grid">
                <div v-for="index in 4" :key="index" class="beta-page-card--loading" />
            </div>

            <div v-else-if="pagePosts.length" class="beta-content__grid">
                <button
                    v-for="post in pagePosts"
                    :key="post.postId"
                    type="button"
                    class="beta-post-card"
                    @click="emit('editPost', post.postId, selectedPage.id)"
                >
                    <div class="beta-post-card__image">
                        <base-image
                            v-if="post.mainImage"
                            :src="post.mainImage"
                            :alt="post.title || 'Publicação'"
                            class-name="h-full w-full object-cover"
                        />
                        <span v-else aria-hidden="true">▧</span>
                        <em v-if="post.sketch == 1">Rascunho</em>
                        <em v-else-if="post.scheduled == 1">Agendado</em>
                    </div>
                    <div class="beta-post-card__copy">
                        <strong>{{ post.title || 'Publicação sem título' }}</strong>
                        <span>{{ post.display_date || 'Sem data definida' }}</span>
                    </div>
                    <span class="beta-post-card__arrow" aria-hidden="true">→</span>
                </button>
            </div>

            <div v-else class="beta-content__empty">
                <span aria-hidden="true">✦</span>
                <h2>Comece com uma publicação</h2>
                <p>Adicione o primeiro item desta página e abra o editor beta para concluir.</p>
                <button
                    type="button"
                    :disabled="props.creatingPost"
                    @click="emit('createPost', selectedPage.id)"
                >
                    {{ props.creatingPost ? 'Criando…' : 'Criar primeira publicação' }}
                </button>
            </div>
        </template>

        <template v-else>
            <div class="beta-content__search">
                <span aria-hidden="true">⌕</span>
                <input v-model="search" type="search" placeholder="Buscar página pelo nome">
            </div>
            <p v-if="searching" class="beta-content__order-hint">Limpe a busca para ordenar</p>

            <div v-if="betaStore.loadingContent" class="beta-content__grid">
                <div v-for="index in 6" :key="index" class="beta-page-card--loading" />
            </div>

            <draggable
                v-else
                :model-value="sortablePages"
                item-key="id"
                handle=".handle-drag"
                :disabled="orderDisabled"
                :animation="150"
                class="beta-content__grid"
                :aria-busy="betaStore.reorderingPages"
                @update:model-value="reorderPages"
            >
                <template #header>
                    <BetaPageCard
                        v-if="filteredHome"
                        :page="filteredHome"
                        :preview="betaStore.pagePreviews[filteredHome.id]"
                        :post-count="betaStore.postsByPage[filteredHome.id]?.length ?? 0"
                        locked
                        @open="openPage"
                        @configure="emit('configurePage', $event)"
                    />
                </template>
                <template #item="{ element }">
                    <BetaPageCard
                        :page="element"
                        :preview="betaStore.pagePreviews[element.id]"
                        :post-count="betaStore.postsByPage[element.id]?.length ?? 0"
                        :order-disabled="orderDisabled"
                        :order-disabled-reason="orderDisabledReason"
                        @open="openPage"
                        @configure="emit('configurePage', $event)"
                    />
                </template>

                <template #footer>
                    <button
                        type="button"
                        class="beta-create-page-card"
                        @click="emit('openCreate')"
                    >
                        <span class="beta-create-page-card__preview">
                            <span>+</span>
                        </span>
                        <span class="beta-create-page-card__copy">
                            <strong>Criar nova página</strong>
                            <small>Escolha o formato e comece pelo essencial.</small>
                        </span>
                    </button>
                </template>
            </draggable>

            <div v-if="!filteredPages.length && !betaStore.loadingContent" class="beta-content__empty">
                <span aria-hidden="true">⌕</span>
                <h2>Nenhuma página encontrada</h2>
                <p>Tente buscar outro nome.</p>
            </div>
        </template>

    </div>
</template>

<style scoped>
.beta-content {
    display: grid;
    gap: 24px;
}

.beta-content__heading {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 24px;
}

.beta-content__heading p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-content__heading h1 {
    margin-top: 6px;
    color: #10233e;
    font-size: clamp(28px, 4vw, 46px);
    font-weight: 850;
    letter-spacing: -0.04em;
    line-height: 1.03;
}

.beta-content__heading span {
    display: block;
    margin-top: 10px;
    color: #70819a;
    font-size: 12px;
}

.beta-content__back {
    margin-bottom: 14px;
    color: #71839b;
    font-size: 10px;
    font-weight: 800;
}

.beta-content__primary {
    min-height: 46px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 18px;
    color: #fff;
    font-size: 11px;
    font-weight: 850;
}

.beta-content__primary:disabled {
    cursor: wait;
    opacity: 0.6;
}

.beta-content__search {
    display: flex;
    max-width: 460px;
    align-items: center;
    gap: 9px;
    border: 1px solid #e0e7f1;
    border-radius: 16px;
    background: #fff;
    padding: 0 14px;
    color: #7c8da5;
}

.beta-content__search input {
    width: 100%;
    height: 44px;
    background: transparent;
    color: #1b314f;
    font-size: 11px;
    outline: none;
}

.beta-content__order-hint {
    margin-top: -16px;
    color: #7c8da5;
    font-size: 11px;
}

.beta-content__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #e0e7f1;
    border-radius: 18px;
    background: #fff;
    padding: 14px 16px;
}

.beta-content__toolbar div {
    display: flex;
    align-items: baseline;
    gap: 6px;
    color: #7a8ca4;
    font-size: 10px;
}

.beta-content__toolbar strong {
    color: #172d4a;
    font-size: 19px;
}

.beta-content__toolbar button {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 850;
}

.beta-content__grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;
}

.beta-post-card {
    position: relative;
    display: grid;
    min-height: 174px;
    overflow: hidden;
    border: 1px solid #e0e7f1;
    border-radius: 22px;
    background: #fff;
    padding: 19px;
    text-align: left;
    transition: 180ms ease;
}

.beta-post-card:hover {
    transform: translateY(-3px);
    border-color: color-mix(in srgb, var(--main-color) 35%, #e0e7f1);
}

.beta-post-card__copy strong {
    color: #172d4a;
    font-size: 14px;
    font-weight: 850;
}

.beta-post-card__copy span {
    margin-top: 5px;
    color: #8190a5;
    font-size: 9px;
}

.beta-post-card__arrow {
    position: absolute;
    right: 18px;
    bottom: 20px;
    color: #8a9ab0;
}

.beta-page-card--loading {
    min-height: 226px;
    border: 1px solid #e4e9ef;
    border-radius: 22px;
    background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 37%, #eef2f7 63%);
    background-size: 400% 100%;
    animation: beta-content-loading 1.4s ease infinite;
}

.beta-create-page-card {
    display: grid;
    min-width: 0;
    overflow: hidden;
    border: 1px dashed color-mix(in srgb, var(--main-color) 32%, #dbe3ed);
    border-radius: 22px;
    background: color-mix(in srgb, var(--main-color) 2.5%, white);
    color: var(--main-color);
    text-align: left;
    transition: border-color 170ms ease, background 170ms ease, transform 170ms ease;
}

.beta-create-page-card:hover,
.beta-create-page-card:focus-visible {
    border-color: var(--main-color);
    background: color-mix(in srgb, var(--main-color) 6%, white);
    transform: translateY(-2px);
}

.beta-create-page-card__preview {
    display: grid;
    height: 148px;
    place-items: center;
    background:
        radial-gradient(circle, color-mix(in srgb, var(--main-color) 13%, white) 0 29px, transparent 30px),
        linear-gradient(145deg, #fbfcfe, color-mix(in srgb, var(--main-color) 5%, #eef2f7));
}

.beta-create-page-card__preview > span {
    font-size: 29px;
    font-weight: 300;
}

.beta-create-page-card__copy {
    display: grid;
    min-height: 78px;
    align-content: center;
    padding: 15px 17px;
}

.beta-create-page-card__copy strong,
.beta-create-page-card__copy small {
    display: block;
}

.beta-create-page-card__copy strong {
    color: #172d4a;
    font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
    font-size: 14px;
    font-weight: 650;
}

.beta-create-page-card__copy small {
    margin-top: 4px;
    color: #8190a5;
    font-size: 9px;
}

.beta-post-card {
    grid-template-rows: 122px auto;
    min-height: 214px;
    padding: 0;
}

.beta-post-card__image {
    position: relative;
    display: grid;
    overflow: hidden;
    place-items: center;
    background: #edf2f7;
    color: #9aa9bc;
    font-size: 28px;
}

.beta-post-card__image em {
    position: absolute;
    top: 10px;
    left: 10px;
    border-radius: 999px;
    background: rgba(16, 35, 62, 0.88);
    padding: 5px 8px;
    color: #fff;
    font-size: 8px;
    font-style: normal;
    font-weight: 850;
    text-transform: uppercase;
}

.beta-post-card__copy {
    display: grid;
    align-content: center;
    padding: 16px 18px;
}

.beta-content__empty {
    display: grid;
    min-height: 270px;
    place-items: center;
    align-content: center;
    border: 1px dashed #d5deea;
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.72);
    padding: 30px;
    text-align: center;
}

.beta-content__empty > span {
    color: var(--main-color);
    font-size: 28px;
}

.beta-content__empty h2 {
    margin-top: 10px;
    color: #172d4a;
    font-size: 18px;
    font-weight: 850;
}

.beta-content__empty p {
    margin-top: 6px;
    color: #7a8ca4;
    font-size: 10px;
}

.beta-content__empty button {
    margin-top: 18px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 11px 15px;
    color: #fff;
    font-size: 10px;
    font-weight: 850;
}

@keyframes beta-content-loading {
    0% { background-position: 100% 0; }
    100% { background-position: 0 0; }
}

@media (max-width: 1024px) {
    .beta-content__grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (min-width: 1600px) {
    .beta-content__grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
}

@media (min-width: 2300px) {
    .beta-content__grid {
        grid-template-columns: repeat(5, minmax(0, 1fr));
    }
}

@media (max-width: 640px) {
    .beta-content__heading {
        align-items: stretch;
        flex-direction: column;
    }

    .beta-content__primary {
        width: 100%;
    }

    .beta-content__grid {
        grid-template-columns: 1fr;
    }

    .beta-create-page-card {
        grid-template-columns: 116px minmax(0, 1fr);
    }

    .beta-create-page-card__preview {
        height: 112px;
    }

    .beta-create-page-card__copy {
        min-height: 112px;
    }
}
</style>
